import { FC, useEffect, useRef, ReactNode, ChangeEvent } from "react";

type InputProps = {
  label?: string;
  id?: string;
  name?: string;
  type?: string;
  placeholder?: string;
  autoComplete?: string;
  value?: string;
  required?: boolean;
  disabled?: boolean;
  minLength?: number;
  maxLength?: number;
  className?: string;
  error?: string;
  iconPosition?: "leading" | "trailing";
  icon?: ReactNode;
  variant?: "primary" | "secondary";
  size?: "sm" | "md" | "lg";
  hint?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
  onIconClick?: () => void;
};

export const Input: FC<InputProps> = ({
  label,
  id,
  name,
  type = "text",
  autoComplete = "off",
  placeholder = "",
  value,
  required = false,
  disabled = false,
  minLength,
  maxLength,
  className = "",
  error,
  iconPosition,
  icon,
  variant = "primary",
  size = "md",
  hint,
  onChange,
  onBlur,
  onIconClick
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (error && document.activeElement !== inputRef.current) {
      inputRef?.current?.focus();
      inputRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);


  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (type === "number") {
      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, maxLength);
    }
    onChange?.(e);
  }

  const sizes = {
    sm: "text-xs",
    md: "text-base shadow-sm",
    lg: "p-4 text-lg shadow-2xl"
  }

  return (
    <div className={`relative flex flex-col gap-1 ${className}`}>
      {label && <label htmlFor={name} className={`text-sm font-medium ${variant === "primary" ? "" : "text-[#D0D5DD]"}`}>{label}</label>}
      <div className={`flex items-center rounded focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2 focus-within:outline-primary-700 focus-within:ring-0 focus-within:border-transparent 
          ${error ? "outline !outline-2 -outline-offset-2 outline-red-500" : "border border-secondary-20"} ${variant === "primary" ? "bg-white" : "text-white bg-black"}`}>
        {iconPosition === "leading" && icon && <button tabIndex={-1} type="button" className={`ml-2 ${variant === "primary" ? "" : "text-white bg-black"}`} onClick={onIconClick}>{icon}</button>}
        <input
          ref={inputRef}
          id={id}
          name={name}
          type={type}
          placeholder={placeholder}
          autoComplete={autoComplete}
          value={value}
          onChange={handleChange}
          onInput={handleChange}
          onBlur={onBlur}
          required={required}
          disabled={disabled}
          minLength={minLength}
          maxLength={maxLength}
          className={`w-full rounded !border-transparent focus:ring-0 ${variant === "primary" ? "" : "text-white bg-black"} ${sizes[size] || ""} [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
        />
        {iconPosition === "trailing" && icon && <button tabIndex={-1} type="button" className={`mr-2 ${variant === "primary" ? "" : "text-white bg-black"}`} onClick={onIconClick}>{icon}</button>}
      </div>
      {error && <p className="absolute -bottom-4 text-sm leading-none text-red-500">{error}</p>}
      {hint && <p className="text-sm text-gray-500">{hint}</p>}
    </div>
  );
}